import React, { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Upload, FileMusic, Sparkles, Loader2, FileText, Wand2, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

export default function UploadDialog({ onUploaded, trigger }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [lyrics, setLyrics] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const lrcInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setTitle("");
    setArtist("");
    setLyrics("");
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverFile(null);
    setCoverPreview("");
    setDragOver(false);
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".mp3") && f.type !== "audio/mpeg") {
      toast.error("Please choose an MP3 file");
      return;
    }
    setFile(f);
    if (!title) {
      const base = f.name.replace(/\.mp3$/i, "").trim();
      setTitle(base);
    }
  };

  const handleLrcFile = async (f) => {
    if (!f) return;
    const text = await f.text();
    setLyrics(text);
    toast.success("Lyrics file loaded");
  };

  const handleCoverFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Cover must be an image (JPG, PNG, WEBP)");
      return;
    }
    if (f.size > 6 * 1024 * 1024) {
      toast.error("Cover too large (max 6MB)");
      return;
    }
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverFile(f);
    setCoverPreview(URL.createObjectURL(f));
  };

  const clearCover = () => {
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverFile(null);
    setCoverPreview("");
  };

  const autoFetch = async () => {
    if (!artist.trim() || !title.trim()) {
      toast.error("Add both artist and title first");
      return;
    }
    setFetching(true);
    try {
      const { data } = await api.get("/lyrics/fetch", {
        params: { artist: artist.trim(), title: title.trim() },
      });
      setLyrics(data.lyrics || "");
      toast.success("Lyrics fetched (unsynced)");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Lyrics not found");
    } finally {
      setFetching(false);
    }
  };

  const onSubmit = async () => {
    if (!file) {
      toast.error("Please select an MP3 file");
      return;
    }
    if (!title.trim()) {
      toast.error("Add a title");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("audio", file);
      fd.append("title", title.trim());
      fd.append("artist", artist.trim());
      fd.append("lyrics", lyrics);
      if (coverFile) fd.append("cover", coverFile);
      const { data } = await api.post("/songs/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Song added to your library");
      onUploaded?.(data);
      reset();
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        {trigger || (
          <Button
            className="bg-[#FF3B30] hover:bg-[#FF5C53] text-white rounded-sm font-display font-bold uppercase tracking-wide text-sm h-11 px-5"
            data-testid="open-upload-dialog-btn"
          >
            <Upload className="h-4 w-4 mr-2" /> New track
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        className="bg-[#0F0F0F] border-white/10 text-white max-w-2xl rounded-sm p-0 overflow-hidden"
        data-testid="upload-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]">
            // upload pipeline
          </div>
          <DialogTitle className="font-display font-black text-3xl tracking-tighter">
            Add a new track
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 pb-6 space-y-5">
          {/* Dropzone */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              handleFile(f);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`cursor-pointer border-2 border-dashed rounded-sm p-6 transition-colors ${
              dragOver
                ? "border-[#FF3B30] bg-[#FF3B30]/5"
                : "border-white/10 hover:border-[#FF3B30]/60"
            }`}
            data-testid="audio-upload-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/mpeg,.mp3"
              hidden
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="audio-file-input"
            />
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-sm bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                <FileMusic className="h-7 w-7 text-[#FF3B30]" />
              </div>
              <div className="flex-1 min-w-0">
                {file ? (
                  <>
                    <div className="font-display font-bold text-base truncate">{file.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB · click to change
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-display font-bold text-base">Drop your MP3 here</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">
                      or click to browse · max 50MB
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Cover art picker */}
          <div
            className="flex items-center gap-4 border border-white/10 rounded-sm p-3"
            data-testid="cover-picker-row"
          >
            <button
              type="button"
              onClick={() => coverInputRef.current?.click()}
              className={`relative h-16 w-16 shrink-0 rounded-sm overflow-hidden border ${
                coverPreview ? "border-[#FF3B30]" : "border-white/10 hover:border-[#FF3B30]/60"
              } bg-[#1A1A1A] flex items-center justify-center transition-colors`}
              data-testid="cover-picker-thumb"
            >
              {coverPreview ? (
                <img
                  src={coverPreview}
                  alt="cover preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <ImageIcon className="h-6 w-6 text-white/40" />
              )}
            </button>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => handleCoverFile(e.target.files?.[0])}
              data-testid="cover-file-input"
            />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm truncate">
                {coverFile ? coverFile.name : "Cover art (optional)"}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">
                {coverFile
                  ? `${(coverFile.size / (1024 * 1024)).toFixed(2)} MB · jpg / png / webp`
                  : "square images look best · max 6MB"}
              </div>
            </div>
            {coverFile ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={clearCover}
                className="text-white/60 hover:text-white hover:bg-transparent h-8 w-8"
                data-testid="cover-clear-btn"
                title="Remove cover"
              >
                <X className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => coverInputRef.current?.click()}
                className="bg-transparent border-white/10 text-white hover:bg-[#1A1A1A] hover:text-white rounded-sm font-mono uppercase text-xs tracking-[0.2em]"
                data-testid="cover-choose-btn"
              >
                <Upload className="h-3.5 w-3.5 mr-2" /> Choose
              </Button>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                Title
              </Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Track title"
                className="mt-2 bg-transparent border-white/10 focus-visible:border-[#FF3B30] focus-visible:ring-0 rounded-sm"
                data-testid="upload-title-input"
              />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                Artist
              </Label>
              <Input
                value={artist}
                onChange={(e) => setArtist(e.target.value)}
                placeholder="Artist name"
                className="mt-2 bg-transparent border-white/10 focus-visible:border-[#FF3B30] focus-visible:ring-0 rounded-sm"
                data-testid="upload-artist-input"
              />
            </div>
          </div>

          <Tabs defaultValue="paste" className="w-full">
            <TabsList className="bg-[#161616] border border-white/10 rounded-sm h-10">
              <TabsTrigger
                value="paste"
                className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white rounded-sm font-mono text-xs uppercase tracking-[0.2em]"
                data-testid="lyrics-tab-paste"
              >
                <FileText className="h-3.5 w-3.5 mr-2" /> Paste
              </TabsTrigger>
              <TabsTrigger
                value="upload"
                className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white rounded-sm font-mono text-xs uppercase tracking-[0.2em]"
                data-testid="lyrics-tab-upload"
              >
                <Upload className="h-3.5 w-3.5 mr-2" /> .lrc
              </TabsTrigger>
              <TabsTrigger
                value="fetch"
                className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white rounded-sm font-mono text-xs uppercase tracking-[0.2em]"
                data-testid="lyrics-tab-fetch"
              >
                <Sparkles className="h-3.5 w-3.5 mr-2" /> Auto-fetch
              </TabsTrigger>
            </TabsList>
            <TabsContent value="paste" className="mt-3">
              <Textarea
                value={lyrics}
                onChange={(e) => setLyrics(e.target.value)}
                placeholder={`Paste lyrics here. Plain text works.\nFor synced lyrics use .lrc format:\n[00:12.34]First line of the song\n[00:16.10]Next line`}
                className="bg-transparent border-white/10 focus-visible:border-[#FF3B30] focus-visible:ring-0 rounded-sm min-h-[160px] font-mono text-sm"
                data-testid="lyrics-input-area"
              />
            </TabsContent>
            <TabsContent value="upload" className="mt-3">
              <input
                ref={lrcInputRef}
                type="file"
                accept=".lrc,.txt,text/plain"
                hidden
                onChange={(e) => handleLrcFile(e.target.files?.[0])}
                data-testid="lrc-file-input"
              />
              <Button
                variant="outline"
                className="rounded-sm border-white/10 bg-transparent hover:bg-[#1A1A1A] hover:text-white"
                onClick={() => lrcInputRef.current?.click()}
                data-testid="open-lrc-upload-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                {lyrics ? "Replace lyrics file" : "Choose .lrc / .txt file"}
              </Button>
              {lyrics && (
                <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">
                  {lyrics.split("\n").length} lines loaded
                </div>
              )}
            </TabsContent>
            <TabsContent value="fetch" className="mt-3">
              <div className="flex gap-2 items-start">
                <Button
                  onClick={autoFetch}
                  disabled={fetching}
                  className="bg-[#1A1A1A] hover:bg-[#222] border border-white/10 rounded-sm text-white font-display font-bold uppercase text-xs tracking-wide"
                  data-testid="auto-fetch-lyrics-btn"
                >
                  {fetching ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Wand2 className="h-4 w-4 mr-2 text-[#FF3B30]" />
                  )}
                  Fetch from lyrics.ovh
                </Button>
                <div className="text-xs text-white/40 mt-2 font-mono">
                  Requires artist + title above. Returns unsynced lyrics.
                </div>
              </div>
              {lyrics && (
                <Textarea
                  value={lyrics}
                  onChange={(e) => setLyrics(e.target.value)}
                  className="mt-3 bg-transparent border-white/10 focus-visible:border-[#FF3B30] focus-visible:ring-0 rounded-sm min-h-[120px] font-mono text-sm"
                />
              )}
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-white/10 bg-[#0A0A0A]">
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            className="text-white/60 hover:bg-transparent hover:text-white rounded-sm font-mono uppercase tracking-[0.2em] text-xs"
            data-testid="upload-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={uploading || !file}
            className="bg-[#FF3B30] hover:bg-[#FF5C53] text-white rounded-sm font-display font-bold uppercase tracking-wide text-sm px-5"
            data-testid="upload-submit-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {uploading ? "Uploading…" : "Add to library"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
